import { Component, ViewEncapsulation } from '@angular/core';
import { CommonLibrary } from '../core/common-library';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ResourcePageDetails } from './DTOs/resource-page-details';
import { ToastNotificationService } from '../core/toast-notification.service';
import { UIBlockingService } from '../core/ui-blocking.service';
import { SuperAdminResourcesService } from './super-admin-resources.service';
import { FileUploadResult } from '../member-news/DTOs/file-upload-result';
import { CreateResourceRequest } from './DTOs/create-resource-request';
import { UpdateResourcePageRequest } from './DTOs/update-resource-page-request';

@Component({
  selector: 'app-resource-details',
  templateUrl: './resource-details.component.html',
  encapsulation: ViewEncapsulation.None
})
export class ResourceDetailsComponent {

    constructor(private route: ActivatedRoute
        , private fb: FormBuilder
        , private notificationService: ToastNotificationService
        , private blockUI: UIBlockingService
        , private router: Router
        , private resourceService: SuperAdminResourcesService) { }

    protected readonly CommonLibrary = CommonLibrary;
    resource: ResourcePageDetails;

    pageNameCtrl = new FormControl("pageName", [Validators.required]);
    categoryCtrl = new FormControl("category", [Validators.required, Validators.min(1)]);
    pageContentCtrl = new FormControl("pageContent", [Validators.required]);
    mainImageFile: File = null;
    preview = "";
    attachments: File[] = [];
    isNew = true;
    attachmentsToRemove = [];
    allowedFileTypes = ['image/jpeg', 'image/png', 'application/pdf', 'application/msword'];

    resourceForm: FormGroup = new FormGroup({
        pageName: this.pageNameCtrl,
        pageContent: this.pageContentCtrl,
        category: this.categoryCtrl,
    })

    pageContent() { return this.resourceForm.get('pageContent').value }
    pageName() { return this.resourceForm.get('pageName').value }
    category() { return this.resourceForm.get('category').value }

    ngOnInit() {
        this.resource = new ResourcePageDetails();
        this.resourceForm.reset();
        var id = parseInt(this.route.snapshot.paramMap.get("id"))
        this.isNew = isNaN(id) || id < 0;

        if (!this.isNew) {
            this.getResourceInfo();
        }
    }

    getResourceInfo() {
        this.blockUI.start();
        this.resourceService.getResourcePageDetails(parseInt(this.route.snapshot.paramMap.get("id"))).subscribe({
            next: (data) => {
                this.resource = data;
                this.resetResourceForm();
            },
            error: (err) => {
                this.notificationService.error(err.error.message);
                this.blockUI.stop();
                console.log(err)
            },
            complete: () => {
                this.blockUI.stop();
            }
        });
    }

    private resetResourceForm(): void {
        this.resourceForm.reset();
        this.resourceForm.patchValue({
            pageName: this.resource.pageName,
            pageContent: this.resource.pageContent,
            category: this.resource.category,
        });
        this.preview = this.resource.mainImageUrl;
        this.attachments = [];
        this.attachmentsToRemove = [];
    }

    DownloadAttachment(ind) {
        CommonLibrary.downloadImageByUrl(URL.createObjectURL(this.attachments[ind]), this.attachments[ind].name.split('.')[0])
    }
    DownloadSavedAttachment(ind) {
        CommonLibrary.downloadImageByUrl(this.resource.attachments[ind].downloadUrl, this.resource.attachments[ind].name.split('.')[0])
    }

    updateMainImage(files: FileList) {
        this.mainImageFile = files.item(0);
        const reader = new FileReader();

        reader.onload = (e: any) => {
            this.preview = e.target.result;
        }
        reader.readAsDataURL(files.item(0));
    }

    updateAttachments(event) {
        var files: FileList = event.target.files;
        for (let i = 0; i < files.length; i++) {
            if (!this.allowedFileTypes.includes(files[i].type)){
                this.notificationService.error(files[i].name + " is not a valid file type.");
                continue;
            }
            this.attachments.push(files.item(i));
        }
        event.target.value = null;
    }

    removeAttachment(index: number) {
        if (index >= 0) {
            this.attachments.splice(index, 1);
        }
    }
    removeSavedAttachment(index: number) {
        if (index >= 0) {
            this.attachmentsToRemove.push(this.resource.attachments[index].attachmentId);
            this.resource.attachments.splice(index, 1);
        }
    }

    updatePage() {
        for (var i in this.resourceForm.controls) {
            this.resourceForm.controls[i].markAsTouched();
        }
        if (!this.resourceForm.valid || (this.mainImageFile == null && this.resource.mainImageId == null)) {
            this.notificationService.error("All fields including a main image required.");
            return;
        }

        this.blockUI.start();

        if (this.mainImageFile != null || this.attachments.length > 0) {
            this.uploadFile()
                .subscribe({
                    next: (data: FileUploadResult) => {
                        this.updateResourcePage(data.mainImageId, data.attachmentIds);
                    },
                    error: (err) => {
                        console.log(err);
                        this.notificationService.error(err.error.message);
                        this.blockUI.stop();
                    }
                });
        }
        else {
            this.updateResourcePage(null, null);
        }
    }

    submitPage() {
        for (var i in this.resourceForm.controls) {
            this.resourceForm.controls[i].markAsTouched();
        }
        if (!this.resourceForm.valid || this.mainImageFile == null) {
            this.notificationService.error("All fields including a main image required.");
            return;
        }

        this.blockUI.start();

        if (this.mainImageFile != null || this.attachments.length > 0) {
            this.uploadFile()
                .subscribe({
                    next: (data: FileUploadResult) => {
                        this.submitResourcePage(data.mainImageId, data.attachmentIds);
                    },
                    error: (err) => {
                        console.log(err);
                        this.notificationService.error(err.error.message);
                        this.blockUI.stop();
                    }
                });
        }
        else {
            this.submitResourcePage(null, null);
        }
    }

    submitResourcePage(mainImageId: string, attachmentIds: string[]) {
        var request = new CreateResourceRequest(this.pageName(), this.pageContent(), this.category(), mainImageId, attachmentIds);

        this.resourceService.createResourcePage(request)
            .subscribe({
                next: (data) => {
                    console.log('Observable emitted the next value: ' + data);
                },
                error: (err) => {
                    console.log(err);
                    this.notificationService.error(err.error.message);
                    this.blockUI.stop();
                },
                complete: () => {
                    console.log('Observable emitted the complete notification');
                    this.blockUI.stop();
                    this.notificationService.success("Page created successfully");
                    
                    this.router.navigate(['/admin-portal/chamber/super-admin-resources']);
                }
            });
    }

    uploadFile() {
        const uploadData = new FormData();
        if (this.mainImageFile != null) {
            uploadData.append('mainImage', this.mainImageFile, this.mainImageFile.name);
        }
        for (let i = 0; i < this.attachments.length; i++) {
            let attachment = this.attachments[i];
            uploadData.append("attachment" + i, attachment, attachment.name);
        }
        return this.resourceService.uploadFiles(uploadData);
    }

    updateResourcePage(mainImageId: string, attachmentIds: string[]) {
        const req = new UpdateResourcePageRequest();
        req.pageId = this.resource.pageId;
        req.pageName = this.pageName();
        req.pageContent = this.pageContent();
        req.category = this.category();
        req.mainImageId = mainImageId != null ? mainImageId : this.resource.mainImageId;
        req.attachmentsToRemove = this.attachmentsToRemove;
        req.attachmentsToAdd = attachmentIds;

        this.resourceService.updateResourcePage(req).subscribe({
            next: (data) => {
            },
            error: (err) => {
                this.notificationService.error(err.error.message);
                this.blockUI.stop();
                console.log(err)
            },
            complete: () => {
                this.blockUI.stop();
                this.notificationService.success("Resource page successfully updated.");
                this.getResourceInfo();
            }
        });
    }

    handleImageInsert = (cb, value, meta) => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');

        input.addEventListener('change', (e) => {
            const file = input.files[0];
            const formData = new FormData();
            formData.append("image", file);
            this.resourceService.uploadInsertedImage(formData)
                .subscribe({
                    next: (url) => {
                        cb(url, { title: file.name });
                    },
                    error: (err) => {
                        console.log(err);
                        this.notificationService.error(err.error.message);
                    }
                });
        });

        input.click();
    }
}
